import { useState } from 'react';

import { ChartFigure } from './ChartFigure';
import { donutSlices, fmtK } from './geometry';

export interface DonutDatum {
  label: string;
  value: number;
  /** Slice colour token. */
  color: string;
  /**
   * The value as the reader should see it — `"5.5k"`, `"1,392"`. Formatting
   * belongs to the caller, which knows what the value counts. Falls back to the
   * same `fmtK` the centre total uses.
   */
  valueLabel?: string;
}

export interface DonutProps {
  data: readonly DonutDatum[];
  summary: string;
  /** Total shown in the centre. Defaults to the sum of values. */
  total?: number;
  /** Caption under the centre total. */
  unit?: string;
}

/**
 * §5.5 By-source donut — one full circle per slice with
 * `dasharray = frac·301.6` and a negative cumulative offset. Circumference is the
 * mock's hard-coded 301.6 (2π·48). A hidden table lists each source + percentage.
 *
 * **Hovering a segment names it and its count, in the hole.** A donut's centre is
 * already a readout — it shows the total at rest — so the hovered segment simply
 * takes it over. Deliberately not the leader-line card the `Pie` uses: that pie is
 * the subject of a 880px dialog with room in its margins, whereas this sits in a
 * narrow dashboard card where a floating card would spill straight out of it. The
 * legend beside it carries percentages only, so the count is the thing hovering
 * actually adds.
 */
export function Donut({ data, summary, total, unit = 'documents' }: DonutProps): React.JSX.Element {
  const slices = donutSlices(data.map((d) => d.value));
  const sum = total ?? data.reduce((acc, d) => acc + d.value, 0);
  const [hovered, setHovered] = useState<number | null>(null);
  const active = hovered !== null ? data[hovered] : undefined;
  return (
    <ChartFigure
      summary={summary}
      columns={['Source', 'Share']}
      tableCaption="Documents by source"
      rows={data.map((d, i) => ({ label: d.label, values: { Share: `${String(slices[i]?.pct ?? 0)}%` } }))}
    >
      <svg width="116" height="116" viewBox="0 0 120 120" role="img" aria-label={summary}>
        <g
          className="ds-donut__ring"
          transform="rotate(-90 60 60)"
          onMouseLeave={() => {
            setHovered(null);
          }}
        >
          {data.map((datum, i) => {
            const slice = slices[i];
            if (slice === undefined) return null;
            return (
              <circle
                key={datum.label}
                className="ds-donut__arc"
                cx="60"
                cy="60"
                r="48"
                fill="none"
                stroke={datum.color}
                strokeWidth="15"
                strokeDasharray={slice.dash}
                strokeDashoffset={slice.off}
                onMouseEnter={() => {
                  setHovered(i);
                }}
              />
            );
          })}
        </g>
        {/* The hole is the readout: the total at rest, the hovered segment while
            hovering. Same two lines either way, so nothing jumps. */}
        <text x="60" y="57" fontSize="16" fontWeight="600" fill="var(--t1)" textAnchor="middle">
          {active !== undefined ? (active.valueLabel ?? fmtK(active.value)) : fmtK(sum)}
        </text>
        <text
          x="60"
          y="72"
          fontSize="10"
          fill={active !== undefined ? active.color : 'var(--t2)'}
          textAnchor="middle"
        >
          {active?.label ?? unit}
        </text>
      </svg>
    </ChartFigure>
  );
}
